//src/components/admin/AdminLayout.tsx
import React, { useState } from 'react';
import { useNavigate, Outlet, Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, CreditCard, Settings, Terminal, LogOut, Zap,
  Menu, X, FileText, Shield, ArrowLeft, Download
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

export function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success('Signed out successfully');
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Failed to sign out');
    }
  };

  const navigationItems = [
    { label: 'Dashboard', icon: LayoutDashboard, path: '/admin' },
    { label: 'User Management', icon: Users, path: '/admin/users' },
    { label: 'Subscription Management', icon: CreditCard, path: '/admin/subscriptions' },
    { label: 'SAM.gov Import', icon: Download, path: '/admin/sam-import' },
    { label: 'Blog Management', icon: FileText, path: '/admin/blog' },
    { label: 'System Maintenance', icon: Terminal, path: '/admin/system' },
    { label: 'Settings', icon: Settings, path: '/admin/settings' },
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
    setShowMobileMenu(false);
  };

  const isActive = (path: string) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  const Logo = ({ admin = false }: { admin?: boolean }) => (
    <div className="flex items-center gap-2.5">
      <div className="w-7 h-7 bg-bw-sidebar-active rounded-lg flex items-center justify-center flex-shrink-0">
        <Zap className="h-4 w-4 text-white" strokeWidth={2.5} />
      </div>
      <span className="text-[15px] font-bold text-white tracking-tight">BidWin</span>
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/10 text-[10px] font-bold text-white/80">
        <Shield className="h-3 w-3" /> {admin ? 'ADMIN' : 'Admin'}
      </span>
    </div>
  );

  return (
    <div className="min-h-screen bg-bw-app flex">
      {/* Desktop Sidebar — ink navy */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex flex-col h-screen bg-bw-sidebar">
            {/* Logo Section */}
            <div className="h-14 flex items-center px-4 border-b border-white/10 flex-shrink-0">
              <Logo />
            </div>

            {/* Return to Main Site */}
            <Link
              to="/dashboard"
              className="flex items-center px-4 py-2.5 text-[13px] text-white/60 hover:bg-white/10 hover:text-white border-b border-white/10 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Return to Main Site
            </Link>

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto py-2">
              {navigationItems.map((item) => {
                const active = isActive(item.path);
                return (
                  <div key={item.label} className="px-2 mb-0.5">
                    <button
                      onClick={() => handleNavigation(item.path)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] transition-colors ${
                        active
                          ? 'bg-bw-sidebar-active text-white font-semibold'
                          : 'text-white/60 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <item.icon className="h-[15px] w-[15px]" />
                      <span>{item.label}</span>
                      {active && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-bw-surface" />}
                    </button>
                  </div>
                );
              })}
            </nav>

            {/* Sign Out */}
            <div className="border-t border-white/10 p-3 flex-shrink-0">
              <button
                onClick={handleSignOut}
                className="w-full flex items-center px-3 py-2 text-[13px] text-white/70 hover:bg-white/10 hover:text-white rounded-lg transition-colors"
              >
                <LogOut className="h-[15px] w-[15px] mr-2" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Header */}
      <div className="lg:hidden">
        <div className="fixed top-0 left-0 right-0 h-14 bg-bw-sidebar z-40 flex items-center justify-between px-4">
          <Logo />
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="p-2 rounded-lg text-white/70 hover:bg-white/10"
          >
            {showMobileMenu ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {showMobileMenu && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setShowMobileMenu(false)}>
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-bw-sidebar h-full" onClick={(e) => e.stopPropagation()}>
            <div className="h-14 flex items-center px-4 border-b border-white/10">
              <Logo admin />
            </div>
            <nav className="mt-3 px-2 space-y-0.5 flex-1 overflow-y-auto">
              {navigationItems.map((item) => {
                const active = isActive(item.path);
                return (
                  <button
                    key={item.label}
                    onClick={() => handleNavigation(item.path)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg ${
                      active
                        ? 'bg-bw-sidebar-active text-white'
                        : 'text-white/60 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <item.icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </nav>
            <div className="flex-shrink-0 border-t border-white/10 p-4">
              <button onClick={handleSignOut} className="w-full flex items-center text-white/70 hover:text-white">
                <LogOut className="h-5 w-5 mr-2" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Desktop Header — clean token strip */}
        <div className="hidden lg:block bg-bw-surface border-b border-bw-border">
          <div className="px-6 py-4">
            <h1 className="text-xl font-bold text-bw-ink tracking-tight">Admin Portal</h1>
            <p className="text-bw-muted text-sm mt-0.5">Manage your BidWin platform</p>
          </div>
        </div>

        {/* Page Content */}
        <div className="flex-1 p-6 pt-20 lg:pt-6 overflow-auto bg-bw-app">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
